import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { ArrowUpDown, ArrowUp, ArrowDown, Star, TrendingUp } from "lucide-react";

const SOURCE_LABELS = {
    accountant:      "Accountant",
    solicitor:       "Solicitor",
    wealth_manager:  "Wealth Manager",
    broker_network:  "Broker Network",
    direct_approach: "Direct Approach",
    website:         "Website",
    linkedin:        "LinkedIn",
    repeat_client:   "Repeat Client",
    search_fund:     "Search Fund",
    bought_lead:     "Bought Lead",
    other:           "Other",
};

const COLUMNS = [
    { key: "source",                label: "Source",          fmt: (v) => SOURCE_LABELS[v] || v },
    { key: "deals_sourced",         label: "Deals Sourced",   fmt: (v) => v },
    { key: "mandates_won",          label: "Mandates Won",    fmt: (v) => v },
    { key: "completions",           label: "Completions",     fmt: (v) => v },
    { key: "completion_rate",       label: "Completion Rate", fmt: (v) => `${(v * 100).toFixed(1)}%` },
    { key: "total_fees_generated",  label: "Total Fees",      fmt: (v) => v > 0 ? `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}` : "—" },
    { key: "avg_fee",               label: "Avg Fee",         fmt: (v) => v > 0 ? `£${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}` : "—" },
];

const BAR_COLORS = ["#0047FF", "#0EA5E9", "#6366F1", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#14B8A6", "#64748B", "#94A3B8", "#CBD5E1"];

export default function Origination() {
    const { user } = useAuth();
    const nav = useNavigate();

    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    useEffect(() => {
        if (!isAdmin) nav("/dashboard", { replace: true });
    }, [isAdmin, nav]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortCol, setSortCol] = useState("total_fees_generated");
    const [sortDir, setSortDir] = useState("desc");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/intelligence/origination");
            setData(res.data);
        } catch (e) {
            toast.error("Failed to load origination data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

    if (!isAdmin) return null;

    const sources = data?.sources || [];
    const bestSource = data?.best_source;

    const sorted = [...sources].sort((a, b) => {
        const aVal = a[sortCol];
        const bVal = b[sortCol];
        if (typeof aVal === "string") {
            return sortDir === "asc"
                ? (SOURCE_LABELS[aVal] || aVal).localeCompare(SOURCE_LABELS[bVal] || bVal)
                : (SOURCE_LABELS[bVal] || bVal).localeCompare(SOURCE_LABELS[aVal] || aVal);
        }
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });

    const handleSort = (key) => {
        if (sortCol === key) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortCol(key);
            setSortDir("desc");
        }
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
        return sortDir === "asc"
            ? <ArrowUp className="w-3 h-3 ml-1 text-foreground" />
            : <ArrowDown className="w-3 h-3 ml-1 text-foreground" />;
    };

    // Chart data — top 8 by fees
    const chartData = [...sources]
        .sort((a, b) => b.total_fees_generated - a.total_fees_generated)
        .slice(0, 8)
        .map(s => ({
            name: SOURCE_LABELS[s.source] || s.source,
            fees: s.total_fees_generated,
            sourced: s.deals_sourced,
        }));

    return (
        <div data-testid="origination-page">
            <PageHeader title="Deal Origination" subtitle="Performance metrics per origination channel." />

            <div className="p-6 lg:p-8 space-y-8">

                {loading ? (
                    <p className="text-sm text-muted-foreground">Loading origination data…</p>
                ) : sources.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md p-12 text-center">
                        <TrendingUp className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No origination data yet. Set the origination source on your deals to start tracking channel performance.</p>
                    </div>
                ) : (
                    <>
                        {/* Best source card */}
                        {bestSource && (
                            <div
                                className="border border-amber-200 bg-amber-50 rounded-md p-6 flex items-center gap-4"
                                data-testid="best-source-card"
                            >
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <Star className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-amber-600 font-medium">Best Performing Channel</p>
                                    <h2 className="text-2xl font-bold font-display mt-0.5" data-testid="best-source-name">
                                        {SOURCE_LABELS[bestSource] || bestSource}
                                    </h2>
                                    {(() => {
                                        const row = sources.find(s => s.source === bestSource);
                                        return row && row.total_fees_generated > 0 ? (
                                            <p className="text-sm text-muted-foreground mt-0.5" data-testid="best-source-fees">
                                                £{Number(row.total_fees_generated).toLocaleString("en-GB", { maximumFractionDigits: 0 })} total fees generated
                                            </p>
                                        ) : null;
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Bar chart */}
                        {chartData.length > 0 && (
                            <div className="border border-border rounded-md bg-white p-6" data-testid="origination-chart">
                                <h2 className="font-display text-xl font-bold tracking-tight mb-4">Fees Generated by Channel</h2>
                                <div className="h-56">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} margin={{ top: 4, right: 4, left: 8, bottom: 40 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 10, fill: "#737373" }}
                                                axisLine={false}
                                                tickLine={false}
                                                angle={-30}
                                                textAnchor="end"
                                                interval={0}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 10, fill: "#737373" }}
                                                axisLine={false}
                                                tickLine={false}
                                                tickFormatter={(v) => v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`}
                                            />
                                            <Tooltip
                                                contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", borderRadius: 4 }}
                                                formatter={(v) => [`£${Number(v).toLocaleString("en-GB")}`, "Fees"]}
                                            />
                                            <Bar dataKey="fees" radius={[3, 3, 0, 0]}>
                                                {chartData.map((_, i) => (
                                                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Sortable table */}
                        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="origination-table">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40 border-b border-border">
                                        <tr>
                                            {COLUMNS.map(col => (
                                                <th
                                                    key={col.key}
                                                    className="px-4 py-3 text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
                                                    onClick={() => handleSort(col.key)}
                                                    data-testid={`sort-${col.key}`}
                                                >
                                                    <span className="flex items-center">
                                                        {col.label}
                                                        <SortIcon col={col.key} />
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/40">
                                        {sorted.map((row) => (
                                            <tr key={row.source} className="hover:bg-muted/20 transition-colors" data-testid={`origination-row-${row.source}`}>
                                                {COLUMNS.map(col => (
                                                    <td key={col.key} className={`px-4 py-3 ${col.key === "source" ? "font-medium" : "font-mono text-right"}`}>
                                                        {col.key === "source" ? (
                                                            <div className="flex items-center gap-2">
                                                                {col.fmt(row[col.key])}
                                                                {row.source === bestSource && (
                                                                    <Star className="w-3 h-3 text-amber-500 fill-amber-400" />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            col.fmt(row[col.key])
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
